import React from 'react'
import { cn } from '@/lib/utils'

export type StatusTone = 'success' | 'warning' | 'danger' | 'brand' | 'violet' | 'neutral'

const TONE: Record<StatusTone, { chip: string; dot: string }> = {
  success: { chip: 'bg-success/10 text-success', dot: 'bg-success' },
  warning: { chip: 'bg-[#F5B400]/15 text-[#B97A00]', dot: 'bg-[#F5B400]' },
  danger: { chip: 'bg-[#D33]/10 text-[#D33]', dot: 'bg-[#D33]' },
  brand: { chip: 'bg-brand/10 text-brand', dot: 'bg-brand' },
  violet: { chip: 'bg-violet/10 text-violet', dot: 'bg-violet' },
  neutral: { chip: 'bg-surface-2 text-ink-3 dark:bg-surface-3', dot: 'bg-ink-4' },
}

type Props = React.HTMLAttributes<HTMLSpanElement> & {
  tone?: StatusTone
  /** Hide the 6px dot (e.g. when the chip carries an icon instead). */
  dot?: boolean
  /** `pulse` = a soft ring breathing behind the dot for live states (online, sending). */
  pulse?: boolean
}

/** 20px status pill: tinted fill, 6px dot, 11px medium label. */
const StatusChip = ({ tone = 'neutral', dot = true, pulse, className, children, ...props }: Props) => {
  const t = TONE[tone]
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[11px] font-medium leading-none tracking-[-0.2px]',
        t.chip,
        className
      )}
      {...props}
    >
      {dot && (
        <span className="relative inline-flex size-1.5">
          {pulse && <span className={cn('absolute inset-0 animate-ping rounded-full opacity-60 [animation-duration:1.6s]', t.dot)} />}
          <span className={cn('relative size-1.5 rounded-full', t.dot)} />
        </span>
      )}
      {children}
    </span>
  )
}

export default StatusChip
